import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaHeart, FaEye, FaChartLine, FaTrash, FaSync } from 'react-icons/fa';
import { db, collection, getDocs, doc, updateDoc, deleteDoc } from '../../firebase';

const StatsManager = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalViews, setTotalViews] = useState(0);
  const [totalLikes, setTotalLikes] = useState(0);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const statsSnapshot = await getDocs(collection(db, 'projectStats'));
      const statsData = statsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate totals
      const views = statsData.reduce((sum, stat) => sum + (stat.views || 0), 0);
      const likes = statsData.reduce((sum, stat) => sum + (stat.likes || 0), 0);

      setStats(statsData);
      setTotalViews(views);
      setTotalLikes(likes);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetStats = async (projectId) => {
    if (!confirm('Are you sure you want to reset stats for this project?')) return;

    try {
      const statsRef = doc(db, 'projectStats', projectId);
      await updateDoc(statsRef, {
        likes: 0,
        views: 0,
        lastReset: new Date()
      });
      fetchStats();
    } catch (error) {
      console.error('Error resetting stats:', error);
    }
  };

  const deleteStats = async (projectId) => {
    if (!confirm('Are you sure you want to delete stats for this project?')) return;

    try {
      await deleteDoc(doc(db, 'projectStats', projectId));
      fetchStats();
    } catch (error) {
      console.error('Error deleting stats:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-xl p-6 border border-violet-500/30"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Projects</p>
              <h3 className="text-3xl font-bold text-white mt-2">{stats.length}</h3>
            </div>
            <FaChartLine className="text-4xl text-violet-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Views</p>
              <h3 className="text-3xl font-bold text-white mt-2">{totalViews}</h3>
            </div>
            <FaEye className="text-4xl text-blue-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-xl p-6 border border-red-500/30"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Likes</p>
              <h3 className="text-3xl font-bold text-white mt-2">{totalLikes}</h3>
            </div>
            <FaHeart className="text-4xl text-red-400" />
          </div>
        </motion.div>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={fetchStats}
          className="px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-colors flex items-center gap-2"
        >
          <FaSync />
          Refresh
        </button>
      </div>

      {/* Stats Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Project ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Views</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Likes</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Last Activity</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {stats.map((stat, index) => (
                <motion.tr
                  key={stat.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4 text-sm text-gray-300 font-mono">{stat.id}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FaEye className="text-blue-400" />
                      <span className="text-white font-medium">{stat.views || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FaHeart className="text-red-400" />
                      <span className="text-white font-medium">{stat.likes || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400">
                    {stat.lastViewed 
                      ? new Date(stat.lastViewed.seconds * 1000).toLocaleDateString()
                      : stat.lastLiked
                      ? new Date(stat.lastLiked.seconds * 1000).toLocaleDateString()
                      : 'N/A'
                    }
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => resetStats(stat.id)}
                        className="p-2 bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30 transition-colors"
                        title="Reset stats"
                      >
                        <FaSync className="text-sm" />
                      </button>
                      <button
                        onClick={() => deleteStats(stat.id)}
                        className="p-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                        title="Delete stats"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {stats.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <FaChartLine className="text-5xl mx-auto mb-4 opacity-50" />
            <p>No project stats available yet</p>
            <p className="text-sm mt-2">Stats will appear as users interact with projects</p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <h4 className="text-blue-400 font-semibold mb-2">ℹ️ How it works</h4>
        <ul className="text-gray-400 text-sm space-y-1">
          <li>• Views are tracked once per session (using sessionStorage)</li>
          <li>• Likes are stored in user's localStorage</li>
          <li>• Stats are automatically created when users interact with projects</li>
          <li>• Reset stats to set likes and views back to 0</li>
          <li>• Delete stats to completely remove the record</li>
        </ul>
      </div>
    </div>
  );
};

export default StatsManager;
